import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    FormGroup,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { UseMutateFunction } from '@tanstack/react-query';
import { z } from 'zod';

import ErrorMessage from '@components/ErrorMessage';
import Loader from '@components/Loader';
import { RefetchingIndicator } from '@components/RefetchingIndicator';
import { usePredefinedPromptsByRole } from '@services/queries';
import { RolesWithPrompts } from '@services/types';

import { predefinedPromptsByRoleTexts } from '../../constants';
import { useOpenAccordion } from '../../hooks/useOpenAccordion';
import { RolesWithPromptsRequest } from '../../services/types';
import { MenuOptionValue } from '../../types';

import { AccordionTitle } from './components/AccordionTitle';
import { PromptSection } from './components/PromptSection';

const PROMPTS_BY_ROLE_CONFIG = {
    label: {
        min: 1,
    },
    text: {
        min: 1,
    },
};

const schema = z.object({
    roles: z.array(
        z.object({
            prompts: z.array(
                z.object({
                    promptId: z.string().optional(),
                    label: z
                        .string()
                        .trim()
                        .min(
                            PROMPTS_BY_ROLE_CONFIG.label.min,
                            predefinedPromptsByRoleTexts.labelInputError
                        ),
                    text: z
                        .string()
                        .trim()
                        .min(
                            PROMPTS_BY_ROLE_CONFIG.text.min,
                            predefinedPromptsByRoleTexts.textInputError
                        ),
                })
            ),
        })
    ),
});

export type FormValues = z.infer<typeof schema>;
interface PredefinedPromptsByRoleFormProps {
    mutate: UseMutateFunction<void, Error, RolesWithPromptsRequest>;
    isSending: boolean;
    data: RolesWithPrompts;
}

const PredefinedPromptsByRoleForm = ({
    mutate,
    isSending,
    data,
}: PredefinedPromptsByRoleFormProps) => {
    const theme = useTheme();

    const { openAccordionId, handleAccordionChange } = useOpenAccordion();

    const defaultValues = useMemo(
        () => ({
            roles: data.map(({ prompts }) => ({
                prompts: prompts.map(({ label, text, promptId }) => ({ label, text, promptId })),
            })),
        }),
        [data]
    );

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        control,
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues,
        mode: 'all',
    });

    useEffect(() => {
        reset(defaultValues);
    }, [reset, defaultValues]);

    const onSubmit = (values: FormValues) => {
        const payload: RolesWithPromptsRequest = data.map((roleItem, roleIndex) => ({
            ...roleItem,
            prompts: values.roles[roleIndex].prompts.map(({ label, text, promptId }) => ({
                label,
                text,
                promptId,
            })),
        }));

        mutate(payload);
    };

    return (
        <Stack
            component='form'
            id={MenuOptionValue.PredefinedPromptsByRole}
            gap={2}
            onSubmit={handleSubmit(onSubmit)}
        >
            <Typography variant='body1'>{predefinedPromptsByRoleTexts.formTitle}</Typography>
            <FormGroup>
                {data.map(({ id: roleId, role }, roleIndex) => (
                    <Accordion
                        key={roleId}
                        expanded={openAccordionId === roleId}
                        onChange={handleAccordionChange(roleId)}
                        slotProps={{ transition: { unmountOnExit: true } }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`${role}-content`}
                            id={`${role}-header`}
                            sx={{ backgroundColor: theme.palette.customColors.lightGray }}
                        >
                            <AccordionTitle role={role} roleIndex={roleIndex} errors={errors} />
                        </AccordionSummary>
                        <AccordionDetails>
                            <PromptSection
                                errors={errors}
                                roleIndex={roleIndex}
                                register={register}
                                isSending={isSending}
                                roleId={roleId}
                                control={control}
                            />
                        </AccordionDetails>
                    </Accordion>
                ))}
            </FormGroup>
        </Stack>
    );
};
interface PredefinedPromptsByRoleProps {
    mutate: UseMutateFunction<void, Error, RolesWithPromptsRequest>;
    isSending: boolean;
}

export const PredefinedPromptsByRole = ({ mutate, isSending }: PredefinedPromptsByRoleProps) => {
    const { data, isLoading, isFetching, error, isError } = usePredefinedPromptsByRole();

    if (isLoading) return <Loader />;
    if (isError) return <ErrorMessage error={error} />;
    if (!data) return null;
    if (data.length === 0)
        return <Typography variant='body2'>{predefinedPromptsByRoleTexts.noRolesFound}</Typography>;

    const isRefetching = !isLoading && isFetching;

    return (
        <>
            {isRefetching && <RefetchingIndicator />}
            <PredefinedPromptsByRoleForm mutate={mutate} isSending={isSending} data={data} />
        </>
    );
};
